// Web bridge stubs for IPC channels that have no browser equivalent
// or are deferred past Milestone 1. Each returns a shape-compatible
// envelope so app.js's `if (result.ok) ...` / `result.error` checks
// remain meaningful.

const notImplemented = (channel) =>
  Promise.resolve({ ok: false, error: `web bridge: ${channel} not implemented yet` });

const notApplicable = (channel) =>
  Promise.resolve({ ok: false, error: `${channel} is not available on web` });

export function buildNoopHandlers() {
  return {
    // ── File / project I/O (M2) ─────────────────────────────────────
    openFolder: () => notImplemented('file:open-folder'),
    saveFile: () => notImplemented('file:save'),
    saveAs: () => notImplemented('file:save-as'),
    exportPdf: () => notImplemented('file:export-pdf'),
    // exportAs gets a real impl in file.js — this noop is the
    // last-resort safety net when file.js's handlers aren't wired
    // (shouldn't happen in practice; createBridge spreads them in).
    exportAs: () => notImplemented('file:export-as'),
    newFromTemplate: () => notImplemented('file:new-from-template'),
    backup: () => Promise.resolve({ ok: true, skipped: true }),
    revert: () => notImplemented('file:revert'),
    print: () => {
      try {
        window.print();
        return Promise.resolve({ ok: true });
      } catch (err) {
        return Promise.resolve({ ok: false, error: err && err.message });
      }
    },
    printPreview: () => notImplemented('file:print-preview'),
    listRecent: () => Promise.resolve([]),

    // ── Drafts (M2) ─────────────────────────────────────────────────
    currentDraft: () => Promise.resolve({ ok: true, name: 'main', displayName: 'Main' }),
    listDrafts: () =>
      Promise.resolve({ ok: true, drafts: [{ name: 'main', displayName: 'Main' }] }),
    createDraft: () => notImplemented('drafts:create'),
    switchDraft: () => notImplemented('drafts:switch'),
    renameDraft: () => notImplemented('drafts:rename'),
    deleteDraft: () => notImplemented('drafts:delete'),

    // ── History (M3) ────────────────────────────────────────────────
    saveVersion: () => Promise.resolve({ ok: true, skipped: true }),
    listVersions: () => Promise.resolve({ ok: true, versions: [] }),
    getVersion: () => notImplemented('history:get-version'),

    // ── AI (M3) ─────────────────────────────────────────────────────
    aiChat: () => notImplemented('ai:chat'),

    // ── Legacy credential surface (retired on web; left as
    //    shape-compatible no-ops so the one-time migration in app.js
    //    runs without errors). ────────────────────────────────────
    setAnthropicKey: () => Promise.resolve({ ok: true }),
    hasAnthropicKey: () => Promise.resolve({ ok: true, hasKey: false }),
    clearAnthropicKey: () => Promise.resolve({ ok: true }),

    // ── Auth (M2) ───────────────────────────────────────────────────
    authSignInEmail: () => notImplemented('auth:sign-in-email'),
    authSignInGoogle: () => notImplemented('auth:sign-in-google'),
    authSignOut: () => Promise.resolve({ ok: true }),
    authGetSession: () => Promise.resolve({ ok: true, session: null }),
    subscriptionRefresh: () => Promise.resolve({ ok: true }),

    // ── Backup (M3) ─────────────────────────────────────────────────
    backupStatus: () => Promise.resolve({ state: 'ok' }),
    backupEnable: () => notImplemented('backup:enable'),
    backupDisable: () => notImplemented('backup:disable'),
    backupTrigger: () => Promise.resolve({ ok: true, skipped: true }),
    backupList: () => Promise.resolve({ ok: true, items: [] }),
    backupListCloud: () => Promise.resolve({ ok: true, items: [] }),
    backupRestore: () => notImplemented('backup:restore'),
    backupCheckUpdates: () => Promise.resolve({ ok: true, hasUpdates: false }),
    backupPull: () => Promise.resolve({ ok: true, skipped: true }),

    // ── Updater (not applicable on web — page reload is the update) ─
    checkForUpdates: () => Promise.resolve({ state: 'not-applicable' }),
    installUpdate: () => Promise.resolve({ state: 'not-applicable' }),
    getUpdateState: () => Promise.resolve({ state: 'not-applicable' }),

    // ── Billing (M3) ────────────────────────────────────────────────
    billingCheckout: () => notImplemented('billing:checkout'),
    billingPortal: () => notImplemented('billing:portal'),

    // ── Remote git (not applicable on web; cloud sync replaces it) ──
    remoteGitStatus: () => notApplicable('remote-git:get-status'),
    remoteGitSet: () => notApplicable('remote-git:set-remote'),
    remoteGitClear: () => notApplicable('remote-git:clear'),
    remoteGitPush: () => notApplicable('remote-git:push'),
    remoteGitPull: () => notApplicable('remote-git:pull'),

    // ── Misc ────────────────────────────────────────────────────────
    openExternal: (url) => {
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
        return Promise.resolve({ ok: true });
      } catch (err) {
        return Promise.resolve({ ok: false, error: err && err.message });
      }
    },
    openUserLink: (url) => {
      try {
        // Renderer-provided link (e.g. from a notes link). Same
        // safety constraints as openExternal — http(s) only.
        if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
          return Promise.resolve({ ok: false, error: 'invalid_url' });
        }
        window.open(url, '_blank', 'noopener,noreferrer');
        return Promise.resolve({ ok: true });
      } catch (err) {
        return Promise.resolve({ ok: false, error: err && err.message });
      }
    },
    // The OS Save dialog has no browser equivalent without the File
    // System Access API (Chromium only). On web there's no real disk
    // path; return a synthetic `cloud://` placeholder so the
    // renderer's cloud-create flow (which requires a non-empty
    // filePath) can proceed. Actual persistence is via Hocuspocus.
    pickSavePath: ({ defaultName } = {}) => {
      const safe = String(defaultName || 'Untitled').replace(/[^a-z0-9._-]+/gi, '_');
      return Promise.resolve({ ok: true, filePath: `cloud://${safe}.fountain` });
    },

    // PDF password prompt: the parser will request via on*PasswordRequest.
    // We don't have a native dialog; the renderer should be displaying
    // its own prompt. Inert stub.
    respondPdfPassword: () => {},

    // Hocuspocus graceful drain — web has no quit lifecycle, so the
    // renderer's `onCloudDrainRequest` listener is wired but never
    // fired. Acknowledge is a no-op.
    cloudDrainComplete: () => {},

    // Test handshakes — wired but inert until the Playwright web-mode
    // launcher (Milestone 5) drives them via the same event bus.
    testRenderComplete: () => {},
    testSerialized: () => {},
  };
}
